import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { vi } from "date-fns/locale";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ImageIcon,
  LayoutGrid,
  List,
  MessageSquare,
  Plus,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { cn, extractErrorMessage, getUploadUrl } from "@/lib/utils";
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_LABELS,
  FEEDBACK_STATUSES,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPES,
  FEEDBACK_TYPE_LABELS,
} from "@/shared/schemas/feedback.schema";
import type { FeedbackStatus } from "@/shared/schemas/feedback.schema";
import {
  FEEDBACK_PAGE_SIZE,
  findFeedbackTransition,
  getAllowedFeedbackTransitions,
} from "@/shared/schemas/feedbackStatus";
import { FeedbackStateTransitionDialog } from "@/components/feedback/FeedbackStateTransitionDialog";
import {
  FEEDBACK_SHEET_BODY_CLASS,
  FEEDBACK_SHEET_CONTENT_CLASS,
  FEEDBACK_SHEET_FOOTER_CLASS,
  FEEDBACK_SHEET_HEADER_CLASS,
  FeedbackPriorityChip,
  FeedbackStatusChip,
  FeedbackTypeChip,
  FeedbackSourceChip,
  FeedbackOccurrenceChip,
  STATUS_DOT_STYLES,
  formatFeedbackDate,
  parseFeedbackImages,
  useIsMobileViewport,
} from "@/components/feedback/feedback-ui";
import { CreateFeedbackDialog } from "./components/CreateFeedbackDialog";
import { formatReporterDisplayName } from "./components/TechnicalResolutionCard";
import { useRowSelection } from "@/hooks/useRowSelection";
import { DataTableRowCheckbox, DataTableSelectAllCheckbox } from "@/components/shared/DataTableRow";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { DatePeriodFilter, computeDatePeriodRange } from "@/components/shared/DatePeriodFilter";
import { MobileOverflowMenu, rankAction, useMobileOverflow } from "@/components/shared/MobileOverflowMenu";
import { ContextualHelpButton } from "@/components/common/ContextualHelpButton";
import { exportToExcel } from "@/lib/excel.utils";
import { useAuthStore } from "@/stores/auth.store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { emsTabClass } from "@/components/ui/ems-tabs";

type ViewMode = "list" | "kanban";

const FEEDBACK_PRESET_PERIODS = ["today", "week", "month", "quarter", "year"] as const;

/** Suy ra kỳ lọc của DatePeriodFilter từ cặp tham số URL dateFrom/dateTo (Rule 11 module-level export) */
export function derivePeriodFromUrlDates(
  dateFrom?: string,
  dateTo?: string,
  now: Date = new Date(),
): { period: string; customFrom: string; customTo: string } {
  const from = String(dateFrom ?? "").trim();
  const to = String(dateTo ?? "").trim();
  if (!from && !to) {
    return { period: "all", customFrom: "", customTo: "" };
  }
  for (const preset of FEEDBACK_PRESET_PERIODS) {
    const range = computeDatePeriodRange(preset, "", "", now);
    if (range.from === from && range.to === to) {
      return { period: preset, customFrom: "", customTo: "" };
    }
  }
  return { period: "custom", customFrom: from, customTo: to };
}

/** Đếm số bộ lọc nâng cao không trùng lặp trong Popover/Sheet (Loại, Mức độ, Module) */
export function countFeedbackAdvancedFilters(filters: {
  type?: string;
  priority?: string;
  module?: string;
}): number {
  let count = 0;
  if (filters.type) count++;
  if (filters.priority) count++;
  if (filters.module) count++;
  return count;
}

/** Chuẩn Feedback v2 §8: chip lọc nguồn phiếu, cuộn ngang trên mobile */
const SOURCE_CHIP_OPTIONS: { key: string; label: string }[] = [
  { key: "", label: "Tất cả" },
  { key: "USER", label: "Người dùng" },
  { key: "QA", label: "QA Tester" },
  { key: "AUTO", label: "Hệ thống" },
];

function FeedbackSourceChips({
  value,
  counts,
  onChange,
}: {
  value: string;
  counts?: Record<string, number>;
  onChange: (key: string) => void;
}) {
  return (
    <div
      data-testid="fb-source-chips"
      role="group"
      aria-label="Nguồn phiếu"
      className="flex items-center gap-1 overflow-x-auto min-w-0 shrink-0 [scrollbar-width:none]"
    >
      {SOURCE_CHIP_OPTIONS.map((opt) => {
        const isSelected = value === opt.key;
        const count = counts ? counts[opt.key || "ALL"] : undefined;
        return (
          <button
            key={opt.key || "ALL"}
            type="button"
            data-testid={`fb-source-tab-${opt.key || "ALL"}`}
            aria-pressed={isSelected}
            onClick={() => onChange(opt.key)}
            className={cn(
              "shrink-0 h-7 px-2.5 rounded-full border text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1.5",
              isSelected
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:text-slate-900",
            )}
          >
            <span>{opt.label}</span>
            {typeof count === "number" && (
              <span
                data-testid={`fb-source-count-${opt.key || "ALL"}`}
                className={cn("tabular-nums text-[11px]", isSelected ? "text-white/80" : "text-slate-400")}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

type FilterKey = "type" | "priority" | "module" | "source" | "dateFrom" | "dateTo";
const FILTER_KEYS: FilterKey[] = ["type", "priority", "module", "source", "dateFrom", "dateTo"];

/** Gom phiếu (đã sắp xếp mới nhất trước) theo ngày gửi, giờ địa phương */
function groupFeedbacksByDay(items: any[]) {
  const groups: { key: string; label: string; items: any[] }[] = [];
  for (const fb of items) {
    const d = new Date(fb.createdAt);
    const key = Number.isNaN(d.getTime()) ? "unknown" : format(d, "yyyy-MM-dd");
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      const label =
        key === "unknown"
          ? "Không rõ ngày"
          : isToday(d)
            ? `Hôm nay, ${format(d, "dd/MM/yyyy")}`
            : isYesterday(d)
              ? `Hôm qua, ${format(d, "dd/MM/yyyy")}`
              : format(d, "EEEE, dd/MM/yyyy", { locale: vi }).replace(/^./, (c) => c.toUpperCase());
      g = { key, label, items: [] };
      groups.push(g);
    }
    g.items.push(fb);
  }
  return groups;
}

function FeedbackDayHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
      <span>{label}</span>
      <span className="rounded-full bg-slate-200/80 px-1.5 py-px text-[11px] font-semibold text-slate-600 tabular-nums">
        {count} phiếu
      </span>
    </div>
  );
}

const selectClass =
  "h-11 md:h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400";

function FeedbackThumb({ fb }: { fb: any }) {
  const first = fb.screenshotBefore || parseFeedbackImages(fb.images)[0];
  if (!first) return null;
  return (
    <img
      src={getUploadUrl(first)}
      alt=""
      loading="lazy"
      className="h-14 w-20 shrink-0 rounded-md border border-slate-200 object-cover bg-slate-50"
    />
  );
}

function imageCount(fb: any): number {
  return new Set([...(fb.screenshotBefore ? [fb.screenshotBefore] : []), ...parseFeedbackImages(fb.images)]).size;
}

/** Thẻ phiếu dùng cho danh sách mobile và kanban */
function FeedbackCard({
  fb,
  onOpen,
  draggable,
  onDragStart,
  showStatus = true,
}: {
  fb: any;
  onOpen: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  showStatus?: boolean;
}) {
  const imgs = imageCount(fb);
  return (
    <article
      data-testid="fb-card"
      data-code={fb.code}
      role="link"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
      className={cn(
        "bg-white rounded-xl border border-slate-200 shadow-xs p-3 space-y-2 cursor-pointer hover:border-blue-300 min-w-0",
        draggable && "md:cursor-grab",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-500">{fb.code}</span>
        {showStatus && <FeedbackStatusChip status={fb.status} />}
      </div>
      <div className="flex gap-3 min-w-0">
        <h4 className="flex-1 min-w-0 text-[15px] font-semibold text-slate-900 leading-snug line-clamp-2 break-words">
          {fb.title}
        </h4>
        <FeedbackThumb fb={fb} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <FeedbackPriorityChip priority={fb.priority} />
        <FeedbackTypeChip type={fb.type} />
        <FeedbackSourceChip source={fb.source} />
        <FeedbackOccurrenceChip
          occurrenceCount={fb.occurrenceCount}
          affectedUsers={fb.affectedUsers}
          source={fb.source}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        <span className="truncate max-w-full">{fb.module || "—"}</span>
        <span>{formatFeedbackDate(fb.createdAt)}</span>
        <span className="inline-flex items-center gap-1" aria-label="Số bình luận">
          <MessageSquare className="h-3.5 w-3.5" /> {fb.commentCount ?? 0}
        </span>
        {imgs > 0 && (
          <span className="inline-flex items-center gap-1" aria-label="Số ảnh">
            <ImageIcon className="h-3.5 w-3.5" /> {imgs}
          </span>
        )}
      </div>
    </article>
  );
}

const KANBAN_DRAG_MIME = "application/x-apv-feedback";

/**
 * Một cột Kanban tự tải phiếu của đúng trạng thái đó (áp dụng chung tìm kiếm + bộ lọc), có "Tải thêm" riêng.
 * Trước đây Kanban lọc từ 20 phiếu của trang danh sách hiện tại nên mỗi cột thiếu phiếu.
 */
function FeedbackKanbanColumn({
  status,
  baseParams,
  canDrag,
  onDrop,
  onOpen,
}: {
  status: FeedbackStatus;
  baseParams: Record<string, any>;
  canDrag: boolean;
  onDrop: (e: React.DragEvent, to: FeedbackStatus) => void;
  onOpen: (id: string) => void;
}) {
  const [chunks, setChunks] = useState(1);
  const signature = JSON.stringify(baseParams);
  useEffect(() => setChunks(1), [signature]);

  const params = { ...baseParams, status, page: 1, limit: FEEDBACK_PAGE_SIZE * chunks };
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["feedbacks", "kanban", status, params],
    queryFn: () => trpcClient.feedback.list.query(params as any),
    placeholderData: keepPreviousData,
  });
  const colItems: any[] = (data as any)?.items || [];
  const total: number = (data as any)?.meta?.total ?? colItems.length;
  const remaining = Math.max(0, total - colItems.length);

  return (
    <section
      data-testid={`fb-kanban-col-${status}`}
      className="w-[85vw] shrink-0 md:w-auto md:shrink snap-start min-w-0 rounded-xl bg-slate-100/70 border border-slate-200 flex flex-col"
      onDragOver={(e) => canDrag && e.preventDefault()}
      onDrop={(e) => canDrag && onDrop(e, status)}
    >
      <header
        data-testid={`fb-kanban-head-${status}`}
        className="md:sticky md:top-[var(--fb-sticky-h)] z-20 rounded-t-xl bg-slate-100 px-3 py-2.5 flex items-center gap-2 border-b border-slate-200"
      >
        <span className={cn("h-2.5 w-2.5 rounded-full", STATUS_DOT_STYLES[status])} />
        <h3 className="text-sm font-semibold text-slate-800">{FEEDBACK_STATUS_LABELS[status]}</h3>
        <span data-testid={`fb-kanban-count-${status}`} className="ml-auto text-xs font-medium text-slate-500">
          {total}
        </span>
      </header>
      {/* Mobile: each column scrolls inside so its header stays visible; bottom padding lets "Tải thêm" scroll above the floating buttons */}
      <div className="p-2 pb-20 md:pb-2 space-y-2 min-h-[120px] max-h-[calc(100dvh-13rem)] overflow-y-auto md:max-h-none md:overflow-visible">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-slate-400">Đang tải...</p>
        ) : colItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Không có phiếu</p>
        ) : (
          colItems.map((fb) => (
            <FeedbackCard
              key={fb.id}
              fb={fb}
              showStatus={false}
              onOpen={() => onOpen(fb.id)}
              draggable={canDrag}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", fb.id);
                // The card may not be on the list page, so carry what the drop handler needs
                e.dataTransfer.setData(KANBAN_DRAG_MIME, JSON.stringify({ id: fb.id, status: fb.status, type: fb.type }));
              }}
            />
          ))
        )}
        {remaining > 0 && (
          <Button
            type="button"
            variant="outline"
            data-testid={`fb-kanban-more-${status}`}
            disabled={isFetching}
            onClick={() => setChunks((c) => c + 1)}
            className="w-full h-11 md:h-9 text-sm"
          >
            {isFetching ? "Đang tải..." : `Tải thêm (còn ${remaining})`}
          </Button>
        )}
      </div>
    </section>
  );
}

export default function FeedbackList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isMobile = useIsMobileViewport();

  // ── Trạng thái lọc giữ trên URL ──
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
  const q = String(search.q ?? "");
  const status = String(search.status ?? "ALL");
  const view: ViewMode = search.view === "kanban" ? "kanban" : "list";
  const page = Math.max(1, Number(search.page) || 1);
  const filters: Record<FilterKey, string> = {
    type: String(search.type ?? ""),
    priority: String(search.priority ?? ""),
    module: String(search.module ?? ""),
    source: String(search.source ?? ""),
    dateFrom: String(search.dateFrom ?? ""),
    dateTo: String(search.dateTo ?? ""),
  };
  const activeFilterCount = countFeedbackAdvancedFilters(filters);
  const hasAnyActiveFilter = FILTER_KEYS.some((k) => Boolean(filters[k]));
  const actionsOverflow = useMobileOverflow(rankAction, 4);

  const setSearch = (patch: Record<string, unknown>, resetPage = true) => {
    (navigate as unknown as (opts: unknown) => void)({
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        if (resetPage) delete next.page;
        for (const k of Object.keys(next)) {
          if (next[k] === "" || next[k] === undefined || next[k] === null || next[k] === "ALL") delete next[k];
        }
        return next;
      },
      replace: true,
    });
  };

  // Đồng bộ nguyên tử DatePeriodFilter (Position 0 Dòng 2 — Rule 12) với tham số URL dateFrom/dateTo
  const derivedDateState = useMemo(
    () => derivePeriodFromUrlDates(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo],
  );
  const [periodOverride, setPeriodOverride] = useState<string | null>(null);
  const [customFromInput, setCustomFromInput] = useState(() => derivedDateState.customFrom);
  const [customToInput, setCustomToInput] = useState(() => derivedDateState.customTo);

  useEffect(() => {
    setCustomFromInput(filters.dateFrom);
    setCustomToInput(filters.dateTo);
    if (!filters.dateFrom && !filters.dateTo && periodOverride !== "custom") {
      setPeriodOverride(null);
    }
  }, [filters.dateFrom, filters.dateTo, periodOverride]);

  const activePeriod = useMemo(() => {
    if (periodOverride === "custom") return "custom";
    if (periodOverride) {
      const expected = computeDatePeriodRange(periodOverride);
      if ((expected.from ?? "") === filters.dateFrom && (expected.to ?? "") === filters.dateTo) {
        return periodOverride;
      }
    }
    return derivedDateState.period;
  }, [periodOverride, filters.dateFrom, filters.dateTo, derivedDateState.period]);

  const handlePeriodChange = (nextPeriod: string) => {
    if (nextPeriod === "all") {
      setPeriodOverride(null);
      setCustomFromInput("");
      setCustomToInput("");
      setSearch({ dateFrom: undefined, dateTo: undefined });
      return;
    }
    if (nextPeriod === "custom") {
      setPeriodOverride("custom");
      return;
    }
    const range = computeDatePeriodRange(nextPeriod);
    setPeriodOverride(nextPeriod);
    setSearch({ dateFrom: range.from, dateTo: range.to });
  };

  const handleCustomFromChange = (val: string) => {
    setPeriodOverride("custom");
    setCustomFromInput(val);
    setSearch({ dateFrom: val || undefined, dateTo: customToInput || undefined });
  };

  const handleCustomToChange = (val: string) => {
    setPeriodOverride("custom");
    setCustomToInput(val);
    setSearch({ dateFrom: customFromInput || undefined, dateTo: val || undefined });
  };

  const clearAllFilters = () => {
    setPeriodOverride(null);
    setCustomFromInput("");
    setCustomToInput("");
    setSearch(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])));
  };

  // Search text comes from the app-wide header search (?q), no in-page search box

  // Mobile: nút "Tải thêm" (vẫn phân trang server theo khối 20 phiếu)
  const filterSignature = JSON.stringify([q, status, filters]);
  const [mobileChunks, setMobileChunks] = useState(1);
  useEffect(() => setMobileChunks(1), [filterSignature]);

  const queryParams = useMemo(() => {
    const params: Record<string, any> = isMobile
      ? { page: 1, limit: FEEDBACK_PAGE_SIZE * mobileChunks }
      : { page, limit: FEEDBACK_PAGE_SIZE };
    if (q) params.search = q;
    if (status !== "ALL") params.status = status;
    for (const k of FILTER_KEYS) if (filters[k]) params[k] = filters[k];
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, mobileChunks, page, filterSignature]);

  // Kanban columns share search + filters but always show every status (no status tab, no list paging)
  const kanbanBaseParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (q) params.search = q;
    for (const k of FILTER_KEYS) if (filters[k]) params[k] = filters[k];
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, JSON.stringify(filters)]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["feedbacks", queryParams],
    queryFn: () => trpcClient.feedback.list.query(queryParams as any),
    placeholderData: keepPreviousData,
  });

  const items: any[] = (data as any)?.items || [];
  const dayGroups = useMemo(() => groupFeedbacksByDay(items), [items]);
  const meta = (data as any)?.meta || { total: 0, totalPages: 1, statusCounts: {} };
  const counts: Record<string, number> = meta.statusCounts || {};
  const totalPages = Math.max(1, meta.totalPages || 1);

  // Frozen page header (like other list pages): table/kanban headers stick right below it
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderH, setStickyHeaderH] = useState(0);
  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setStickyHeaderH(el.offsetHeight));
    ro.observe(el);
    setStickyHeaderH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const [createOpen, setCreateOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  useEffect(() => {
    if (filterOpen) setDraft(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOpen]);

  // ── Kéo thả kanban (chỉ là cách phụ trên desktop) ──
  const [pending, setPending] = useState<{ id: string; type: string; from: FeedbackStatus; to: FeedbackStatus } | null>(null);
  const canDrag = !isMobile && getAllowedFeedbackTransitions("OPEN", currentUser as any).length > 0;

  const updateStatusMutation = useMutation({
    mutationFn: (vars: { id: string; status: FeedbackStatus; note: string; images: string[] }) =>
      trpcClient.feedback.updateStatus.mutate(vars),
    onSuccess: () => {
      toast.success("Đã cập nhật trạng thái");
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
    },
    onError: (err: any) => toast.error("Không thể chuyển trạng thái: " + extractErrorMessage(err)),
  });

  const handleDrop = (e: React.DragEvent, to: FeedbackStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    let fb: { id: string; status: FeedbackStatus; type: string } | undefined = items.find((f) => f.id === id);
    if (!fb) {
      try {
        fb = JSON.parse(e.dataTransfer.getData(KANBAN_DRAG_MIME) || "null") || undefined;
      } catch {
        fb = undefined;
      }
    }
    if (!fb || fb.status === to) return;
    const allowed = getAllowedFeedbackTransitions(fb.status, currentUser as any).some((t) => t.to === to);
    if (!findFeedbackTransition(fb.status, to) || !allowed) {
      toast.error(
        `Không thể chuyển từ "${FEEDBACK_STATUS_LABELS[fb.status as FeedbackStatus]}" sang "${FEEDBACK_STATUS_LABELS[to]}"`,
      );
      return;
    }
    setPending({ id, type: fb.type, from: fb.status, to });
  };

  // Chọn nhiều dòng (bảng desktop) + xuất Excel
  const rowIds = useMemo(() => items.map((f) => f.id), [items]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection, selectionMode, handleRowSelect } = rowSelection;
  const exportFeedbacks = (rows: any[]) => {
    if (!rows.length) {
      toast.warning("Không có phiếu để xuất");
      return;
    }
    exportToExcel(
      rows.map((fb) => ({
        code: fb.code,
        title: fb.title,
        type: FEEDBACK_TYPE_LABELS[fb.type as keyof typeof FEEDBACK_TYPE_LABELS] || fb.type,
        priority: FEEDBACK_PRIORITY_LABELS[fb.priority as keyof typeof FEEDBACK_PRIORITY_LABELS] || fb.priority,
        status: FEEDBACK_STATUS_LABELS[fb.status as FeedbackStatus] || fb.status,
        module: fb.module || "",
        reporter: formatReporterDisplayName(fb.reportedByName, fb.reportedBy),
        createdAt: fb.createdAt ? format(new Date(fb.createdAt), "dd/MM/yyyy HH:mm") : "",
      })),
      [
        { key: "code", label: "Mã", width: 12 },
        { key: "title", label: "Tiêu đề", width: 50 },
        { key: "type", label: "Loại", width: 14 },
        { key: "priority", label: "Mức độ", width: 12 },
        { key: "status", label: "Trạng thái", width: 16 },
        { key: "module", label: "Module", width: 20 },
        { key: "reporter", label: "Người gửi", width: 22 },
        { key: "createdAt", label: "Thời gian", width: 18 },
      ],
      "phan_hoi",
    );
  };

  // Chi tiết mở dạng popup (route con), giữ nguyên bộ lọc/tìm kiếm của danh sách bên dưới
  const openDetail = (id: string) =>
    navigate({ to: "/feedback/$feedbackId", params: { feedbackId: id }, search: (prev: any) => prev } as any);

  const statusTabs = ["ALL", ...FEEDBACK_STATUSES] as const;

  const renderCoreFilterFields = (
    values: Record<FilterKey, string>,
    onChange: (k: FilterKey, v: string) => void,
    compact = false,
  ) => {
    const fieldSelectCls = compact
      ? "h-8 w-full rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
      : selectClass;
    return (
      <>
        <div className="space-y-1 min-w-0">
          <Label htmlFor="fb-filter-type" className={compact ? "text-xs font-medium text-slate-600" : "text-sm text-slate-600"}>
            Loại phiếu
          </Label>
          <select id="fb-filter-type" className={fieldSelectCls} value={values.type} onChange={(e) => onChange("type", e.target.value)}>
            <option value="">Tất cả loại</option>
            {FEEDBACK_TYPES.map((t) => (
              <option key={t} value={t}>{FEEDBACK_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1 min-w-0">
          <Label htmlFor="fb-filter-priority" className={compact ? "text-xs font-medium text-slate-600" : "text-sm text-slate-600"}>
            Mức độ ưu tiên
          </Label>
          <select id="fb-filter-priority" className={fieldSelectCls} value={values.priority} onChange={(e) => onChange("priority", e.target.value)}>
            <option value="">Tất cả mức độ</option>
            {FEEDBACK_PRIORITIES.map((p) => (
              <option key={p} value={p}>{FEEDBACK_PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1 min-w-0">
          <Label htmlFor="fb-filter-module" className={compact ? "text-xs font-medium text-slate-600" : "text-sm text-slate-600"}>
            Phân hệ / Module
          </Label>
          <Input
            id="fb-filter-module"
            className={compact ? "h-8 text-xs" : "h-11 md:h-9 text-sm"}
            value={values.module}
            placeholder="VD: Đơn hàng"
            onChange={(e) => onChange("module", e.target.value)}
          />
        </div>
      </>
    );
  };

  return (
    <div
      data-testid="fb-list-page"
      className="w-full min-w-0 pb-4 md:pb-5"
      style={{ ["--fb-sticky-h" as string]: `${stickyHeaderH}px` }}
    >
      {/* Thanh công cụ cố định chuẩn EMS 2 dòng (~76px): Dòng 1 Nguồn + Thao tác | Dòng 2 Ngày Pos 0 + Lọc + Trạng thái */}
      <div
        ref={stickyHeaderRef}
        data-testid="fb-sticky-header"
        className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-2xs flex flex-col"
      >
        {/* Dòng 1: Tabs Nguồn phiếu + Huy hiệu 24h (Trái) | Chế độ xem + Xuất Excel + Tạo phiếu + ? (Phải) */}
        <div className="erp-list-toolbar-row flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-2 gap-y-1.5 px-3 py-1.5 min-h-[38px] w-full border-b border-slate-100">
          <div className="erp-list-primary flex items-center gap-1.5 grow shrink min-w-0 max-w-full overflow-x-auto hide-scrollbar flex-nowrap">
            <FeedbackSourceChips
              value={filters.source || ""}
              counts={meta.sourceCounts}
              onChange={(key) => setSearch({ source: key || undefined })}
            />
            {Number(meta.auto24hCount || 0) > 0 && (
              <button
                type="button"
                data-testid="fb-auto-24h-badge"
                onClick={() => setSearch({ source: "AUTO" })}
                className="shrink-0 inline-flex items-center gap-1.5 h-6 px-2 rounded-full border border-purple-200 bg-purple-50 text-purple-700 text-[11px] font-semibold cursor-pointer hover:bg-purple-100"
                title="Số phiếu lỗi tự động phát sinh trong 24 giờ qua"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-purple-500 animate-pulse" />
                <span>{meta.auto24hCount} lỗi tự động (24h)</span>
              </button>
            )}
          </div>

          <div
            ref={actionsOverflow.ref}
            className="erp-list-actions flex items-center gap-1.5 shrink-0 ml-auto flex-nowrap max-w-full overflow-x-auto hide-scrollbar"
          >
            <div
              className="flex shrink-0 rounded-md border border-slate-200 bg-white p-0.5"
              role="group"
              aria-label="Chế độ xem"
            >
              <button
                type="button"
                data-testid="fb-view-list"
                aria-pressed={view === "list"}
                aria-label="Danh sách"
                onClick={() => setSearch({ view: undefined }, false)}
                className={cn(
                  "h-6 w-7 flex items-center justify-center rounded-xs cursor-pointer transition-colors",
                  view === "list" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100",
                )}
              >
                <List className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-testid="fb-view-kanban"
                aria-pressed={view === "kanban"}
                aria-label="Kanban"
                onClick={() => setSearch({ view: "kanban" }, false)}
                className={cn(
                  "h-6 w-7 flex items-center justify-center rounded-xs cursor-pointer transition-colors",
                  view === "kanban" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100",
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="fb-export-btn"
              onClick={() => exportFeedbacks(items)}
              className="h-7 px-2 text-[11px] sm:text-xs gap-1 shrink-0 font-medium cursor-pointer"
              aria-label="Xuất Excel"
              title="Xuất Excel các phiếu đang hiển thị"
            >
              <Download className="h-3.5 w-3.5 shrink-0" />
              <span>Xuất<span className="hidden sm:inline"> Excel</span></span>
            </Button>

            {/* Mọi người dùng tạo được phiếu phản hồi (máy chủ không giới hạn quyền tạo) */}
            <Button
              type="button"
              size="sm"
              data-testid="fb-create-btn"
              onClick={() => setCreateOpen(true)}
              className="h-7 px-2.5 text-[11px] sm:text-xs gap-1 shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-medium cursor-pointer"
              aria-label="Tạo phiếu"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span>Tạo phiếu</span>
            </Button>

            <ContextualHelpButton />
            <MobileOverflowMenu items={actionsOverflow.hidden} onSelect={actionsOverflow.activate} />
          </div>
        </div>

        {/* Dòng 2: DatePeriodFilter tại Position 0 (Rule 12) + Nút [Lọc (n)] + Chips Trạng thái */}
        <div className="erp-list-filter-row flex items-center gap-1.5 sm:gap-2 px-3 py-1 min-h-[36px] w-full min-w-0 overflow-x-auto hide-scrollbar flex-nowrap">
          <div className="date-filter-pos0 flex items-center gap-1.5 shrink-0">
            <DatePeriodFilter
              period={activePeriod}
              setPeriod={handlePeriodChange}
              customFrom={customFromInput}
              setCustomFrom={handleCustomFromChange}
              customTo={customToInput}
              setCustomTo={handleCustomToChange}
            />
            <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
          </div>

          {isMobile ? (
            <button
              type="button"
              data-testid="fb-filter-btn"
              onClick={() => setFilterOpen(true)}
              className={cn(
                "h-7 shrink-0 inline-flex items-center gap-1 px-2 rounded-md border text-xs font-medium cursor-pointer",
                activeFilterCount
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-700",
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
              <span>Lọc</span>
              <span>({activeFilterCount})</span>
            </button>
          ) : (
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="fb-filter-btn"
                  className={cn(
                    "h-7 shrink-0 inline-flex items-center gap-1 px-2 rounded-md border text-xs font-medium cursor-pointer transition-colors",
                    activeFilterCount
                      ? "border-blue-400 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                  )}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
                  <span>Bộ lọc</span>
                  <span>({activeFilterCount})</span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="fb-filter-inline"
                align="start"
                className="w-[340px] p-3 space-y-2.5 shadow-lg border-slate-200"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Bộ lọc nâng cao</span>
                  {hasAnyActiveFilter && (
                    <button
                      type="button"
                      onClick={() => {
                        clearAllFilters();
                        setFilterOpen(false);
                      }}
                      className="text-xs font-medium text-rose-600 hover:underline inline-flex items-center gap-1 cursor-pointer"
                    >
                      <X className="h-3 w-3" /> Xóa bộ lọc
                    </button>
                  )}
                </div>
                {renderCoreFilterFields(filters, (k, v) => setSearch({ [k]: v }), true)}
              </PopoverContent>
            </Popover>
          )}

          {view !== "kanban" && (
            <div className="flex items-center gap-1.5 shrink-0" role="tablist" aria-label="Lọc theo trạng thái">
              {statusTabs.map((s) => {
                const active = status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`fb-status-tab-${s}`}
                    onClick={() => setSearch({ status: s })}
                    className={emsTabClass(active, { size: "sm" })}
                  >
                    {s !== "ALL" && <span className={cn("h-2 w-2 rounded-full", STATUS_DOT_STYLES[s as FeedbackStatus])} />}
                    {s === "ALL" ? "Tất cả" : FEEDBACK_STATUS_LABELS[s as FeedbackStatus]}
                    <span className={cn("text-[11px] tabular-nums", active ? "text-primary/80" : "text-slate-500")}>
                      {counts[s] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Bộ lọc dạng bottom sheet trên mobile: đưa Thời gian lên vị trí số 1 trên đỉnh (Rule 12 Mobile) */}
        <Dialog open={filterOpen && isMobile} onOpenChange={setFilterOpen}>
          <DialogContent className={FEEDBACK_SHEET_CONTENT_CLASS} data-testid="fb-filter-sheet">
            <DialogHeader className={FEEDBACK_SHEET_HEADER_CLASS}>
              <DialogTitle className="text-base font-bold">Bộ lọc</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Lọc phiếu theo thời gian, loại, mức độ, phân hệ.</DialogDescription>
            </DialogHeader>
            <div className={FEEDBACK_SHEET_BODY_CLASS}>
              <div className="grid grid-cols-2 gap-2.5 min-w-0">
                <div className="space-y-1 min-w-0">
                  <Label htmlFor="fb-filter-from" className="text-sm text-slate-600">Từ ngày</Label>
                  <Input
                    id="fb-filter-from"
                    type="date"
                    className="h-11 text-sm"
                    value={draft.dateFrom}
                    onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
                  />
                </div>
                <div className="space-y-1 min-w-0">
                  <Label htmlFor="fb-filter-to" className="text-sm text-slate-600">Đến ngày</Label>
                  <Input
                    id="fb-filter-to"
                    type="date"
                    className="h-11 text-sm"
                    value={draft.dateTo}
                    onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
                  />
                </div>
              </div>
              {renderCoreFilterFields(draft, (k, v) => setDraft((d) => ({ ...d, [k]: v })))}
            </div>
            <div className={FEEDBACK_SHEET_FOOTER_CLASS}>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 flex-1"
                onClick={() => setDraft({ type: "", priority: "", module: "", source: "", dateFrom: "", dateTo: "" })}
              >
                Xóa lọc
              </Button>
              <Button
                type="button"
                data-testid="fb-filter-apply"
                className="min-h-11 flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  setPeriodOverride(null);
                  setSearch({ ...draft });
                  setFilterOpen(false);
                }}
              >
                Áp dụng
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Nội dung (lề ngang riêng để phần đầu cố định phủ kín bề ngang mà không tràn) */}
      <div className="px-4 md:px-6 pt-3 space-y-3">
      {error && (
        <p className="text-sm text-red-600">Không tải được danh sách: {extractErrorMessage(error)}</p>
      )}

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Đang tải...</p>
      ) : view === "kanban" ? (
        /* Kanban: mỗi cột tự tải phiếu theo trạng thái (đủ 4 trạng thái, không phụ thuộc trang danh sách) */
        <div
          data-testid="fb-kanban"
          className="w-full min-w-0 flex md:grid md:grid-cols-4 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none pb-2"
        >
          {FEEDBACK_STATUSES.map((s) => (
            <FeedbackKanbanColumn
              key={s}
              status={s}
              baseParams={kanbanBaseParams}
              canDrag={canDrag}
              onDrop={handleDrop}
              onOpen={openDetail}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 bg-white">
          Không có phiếu phản hồi nào.
        </p>
      ) : (
        <>
          {/* Mobile/tablet: danh sách thẻ */}
          <div data-testid="fb-card-list" className="lg:hidden space-y-2">
            {dayGroups.map((g) => (
              <Fragment key={g.key}>
                <div data-testid="fb-day-group" data-day={g.key} className="pt-2 first:pt-0 px-1">
                  <FeedbackDayHeader label={g.label} count={g.items.length} />
                </div>
                {g.items.map((fb) => (
                  <FeedbackCard key={fb.id} fb={fb} onOpen={() => openDetail(fb.id)} />
                ))}
              </Fragment>
            ))}
          </div>

          {/* Desktop ≥1024: bảng */}
          {/* overflow-clip (not hidden) keeps rounded corners without breaking sticky headers */}
          <div className="hidden lg:block rounded-xl border border-slate-200 bg-white overflow-clip">
            <table data-testid="fb-table" className={cn("w-full table-fixed text-sm", selectionMode && "apv-select-mode")}>
              <thead
                data-testid="fb-table-head"
                className="bg-slate-50 text-left text-slate-600 [&_th]:sticky [&_th]:top-[var(--fb-sticky-h)] [&_th]:z-20 [&_th]:bg-slate-50 [&_th]:shadow-[inset_0_-1px_0_rgb(226_232_240)]"
              >
                <tr>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} className="w-[40px] px-2 py-2.5" />
                  <th className="w-[110px] px-3 py-2.5 font-semibold">Mã</th>
                  <th className="px-3 py-2.5 font-semibold">Tiêu đề</th>
                  <th className="w-[100px] px-3 py-2.5 font-semibold">Loại</th>
                  <th className="w-[115px] px-3 py-2.5 font-semibold">Mức độ</th>
                  <th className="w-[130px] px-3 py-2.5 font-semibold">Trạng thái</th>
                  <th className="w-[150px] px-3 py-2.5 font-semibold">Module</th>
                  <th className="w-[150px] px-3 py-2.5 font-semibold">Người gửi</th>
                  <th className="w-[80px] px-3 py-2.5 font-semibold">Giờ</th>
                </tr>
              </thead>
              {dayGroups.map((g) => (
              <tbody key={g.key} data-testid="fb-day-tbody" data-day={g.key}>
                <tr data-testid="fb-day-group" className="border-t border-slate-200 bg-slate-50/70">
                  <td colSpan={9} className="px-3 py-1.5">
                    <FeedbackDayHeader label={g.label} count={g.items.length} />
                  </td>
                </tr>
                {g.items.map((fb) => (
                  <tr
                    key={fb.id}
                    data-testid="fb-row"
                    data-code={fb.code}
                    onClick={(e) => (selectionMode ? handleRowSelect(fb.id, e) : openDetail(fb.id))}
                    className={cn(
                      "border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer",
                      selectedIds.has(fb.id) && "bg-indigo-50/60",
                    )}
                  >
                    <DataTableRowCheckbox id={fb.id} rowSelection={rowSelection} className="px-2 py-2.5" />
                    <td className="px-3 py-2.5 font-semibold text-slate-700">{fb.code}</td>
                    <td className="px-3 py-2.5 min-w-0">
                      <div className="font-medium text-slate-900 truncate" title={fb.title}>{fb.title}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{fb.commentCount ?? 0}</span>
                        {imageCount(fb) > 0 && (
                          <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />{imageCount(fb)}</span>
                        )}
                        <FeedbackSourceChip source={fb.source} />
                        <FeedbackOccurrenceChip
                          occurrenceCount={fb.occurrenceCount}
                          affectedUsers={fb.affectedUsers}
                          source={fb.source}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><FeedbackTypeChip type={fb.type} /></td>
                    <td className="px-3 py-2.5"><FeedbackPriorityChip priority={fb.priority} /></td>
                    <td className="px-3 py-2.5"><FeedbackStatusChip status={fb.status} /></td>
                    <td className="px-3 py-2.5 truncate text-slate-700" title={fb.module}>{fb.module || "—"}</td>
                    <td className="px-3 py-2.5 truncate text-slate-700">
                      {formatReporterDisplayName(fb.reportedByName, fb.reportedBy)}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap tabular-nums" title={formatFeedbackDate(fb.createdAt)}>
                      {fb.createdAt ? format(new Date(fb.createdAt), "HH:mm") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              ))}
            </table>
          </div>
        </>
      )}

      {/* Phân trang: mobile "Tải thêm"; desktop trang trước/sau (20 phiếu/trang). Kanban có "Tải thêm" riêng từng cột */}
      {view === "kanban" ? null : isMobile ? (
        items.length < (meta.total || 0) && (
          <Button
            type="button"
            variant="outline"
            data-testid="fb-load-more"
            disabled={isFetching}
            onClick={() => setMobileChunks((c) => c + 1)}
            className="w-full min-h-11"
          >
            {isFetching ? "Đang tải..." : `Tải thêm (${items.length}/${meta.total})`}
          </Button>
        )
      ) : (
        totalPages > 1 && (
          <nav className="flex items-center justify-end gap-2 text-sm" aria-label="Phân trang" data-testid="fb-pagination">
            <span className="text-slate-500 mr-2">{meta.total} phiếu</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setSearch({ page: page - 1 }, false)}
              aria-label="Trang trước"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>Trang {page}/{totalPages}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setSearch({ page: page + 1 }, false)}
              aria-label="Trang sau"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </nav>
        )
      )}

      <FeedbackStateTransitionDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        oldStatus={pending?.from || null}
        newStatus={pending?.to || null}
        feedbackType={pending?.type}
        isSubmitting={updateStatusMutation.isPending}
        onConfirm={async ({ note, images }) => {
          if (!pending) return;
          await updateStatusMutation.mutateAsync({ id: pending.id, status: pending.to, note, images });
        }}
      />

      <CreateFeedbackDialog open={createOpen} onOpenChange={setCreateOpen} />

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            icon: <Download className="h-4 w-4" />,
            label: `Xuất Excel ${selectedCount} phiếu`,
            onClick: () => exportFeedbacks(items.filter((f) => selectedIds.has(f.id))),
          },
        ]}
      />
      </div>

      {/* Chi tiết phiếu (popup) */}
      <Outlet />
    </div>
  );
}
